import { FormProps } from '@alphabait/ui/form'
import {
  Smiley,
  SmileyBlank,
  SmileyMeh,
  SmileySad,
  SmileyWink,
} from '@phosphor-icons/react'
import cx from 'classnames'
import { Control, FieldValues, Path, useController } from 'react-hook-form'

import { RadioGroupHappinessInput } from './custom-types'
import style from './form.module.css'

const iconThemes: Record<string, Record<string, JSX.Element>> = {
  faces: {
    bad: <SmileySad className={style.happinessIcons} />,
    belowAverage: <SmileyBlank className={style.happinessIcons} />,
    average: <SmileyMeh className={style.happinessIcons} />,
    aboveAverage: <Smiley className={style.happinessIcons} />,
    good: <SmileyWink className={style.happinessIcons} />,
  },
}

type LegacyProps = {
  field: RadioGroupHappinessInput
  formProps: FormProps
}

type DirectProps<TFieldValues extends FieldValues = FieldValues> = {
  name: Path<TFieldValues>
  control: Control<TFieldValues>
  options?: RadioGroupHappinessInput['options']
  iconTheme?: RadioGroupHappinessInput['iconTheme']
  disabled?: boolean
  className?: string
  defaultValue?: string | number
}

type RadioGroupHappinessProps<TFieldValues extends FieldValues = FieldValues> =
  | LegacyProps
  | DirectProps<TFieldValues>

function isLegacyProps<TFieldValues extends FieldValues>(
  props: RadioGroupHappinessProps<TFieldValues>,
): props is LegacyProps {
  return 'field' in props
}

export default function RadioGroupHappiness<
  TFieldValues extends FieldValues = FieldValues,
>(props: RadioGroupHappinessProps<TFieldValues>) {
  const fieldName = isLegacyProps(props) ? props.field.name : props.name
  const control = isLegacyProps(props) ? props.formProps.control : props.control
  const options =
    (isLegacyProps(props) ? props.field.options : props.options) ?? []
  const iconTheme = isLegacyProps(props)
    ? props.field.iconTheme
    : props.iconTheme ?? 'faces'
  const disabled = isLegacyProps(props)
    ? !!props.field.disabled
    : !!props.disabled
  const containerClassName = cx(
    style.radioInputHappiness,
    style.cursorDefault,
    'm-auto',
    !isLegacyProps(props) && props.className,
  )

  const defaultValue = isLegacyProps(props)
    ? props.field.value
    : props.defaultValue

  const {
    field: { value, onChange, onBlur },
  } = useController({
    control,
    name: fieldName as any,
    defaultValue: defaultValue,
  })

  return (
    <div className={containerClassName} data-testid={`${fieldName}-group`}>
      {options.map((option) => {
        const optionValue = option.value
        // Compare as numbers for proper selection
        const numericValue = Number(value)
        const numericOptionValue = Number(optionValue)
        const isSelected =
          !isNaN(numericValue) && numericValue === numericOptionValue
        const icon = option.Icon ? iconThemes[iconTheme]?.[option.Icon] : null
        const optionTestId = `${fieldName}-option-${optionValue}`

        return (
          <label
            htmlFor={`${fieldName}-${option.name}`}
            className={cx(style.radioInputHappinessLabel, {
              [style.selected]: !disabled && isSelected,
              [style.cursorDefault]: disabled,
            })}
            key={`${fieldName}-${option.name}`}
            data-testid={optionTestId}
          >
            <input
              type="radio"
              value={optionValue}
              checked={isSelected}
              onChange={(event) => {
                // Convert to number before setting value
                const numValue = Number(event.target.value)
                onChange(numValue)
              }}
              onBlur={onBlur}
              disabled={disabled}
              data-testid={`${optionTestId}-input`}
            />
            {icon && (
              <div className={style.icon}>
                <div className={style.iconElement}>
                  <div>{icon}</div>
                </div>
                {/* No visible label, as on the end-user page (Alpha-910). */}
                <span className="sr-only">{option.name}</span>
              </div>
            )}
          </label>
        )
      })}
    </div>
  )
}
